import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { WebinarResource } from '@/features/webinar/types';
import type { LiveSessionResourceStateDto, TriggerEventInput } from '@/features/live/types';

interface VideosListProps {
  videos: WebinarResource[];
  loading?: boolean;
  handleTriggerEvent: (event: TriggerEventInput) => void;
  adminResourceStates: LiveSessionResourceStateDto[];
  isModeratorLive?: boolean;
}

export default function VideosList({
  videos,
  loading = false,
  adminResourceStates,
  handleTriggerEvent,
  isModeratorLive = false,
}: VideosListProps) {
  const { t } = useTranslation('webinars');
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);
  const handleToggleVideoLive = (video: WebinarResource) => {
    const isVideoLive = adminResourceStates.some(
      (state) =>
        state.resourceType === 'WEBINAR_RESOURCE' && state.resourceId === video.id && state.live,
    );
    if (isVideoLive) {
      handleTriggerEvent({
        eventType: 'RESOURCE_HIDE',
        resourceType: 'WEBINAR_RESOURCE',
        resourceId: video.id,
        payloadJson: JSON.stringify({
          timeNow: new Date().getTime(),
        }),
      });
    } else {
      handleTriggerEvent({
        eventType: 'RESOURCE_SHOW',
        resourceType: 'WEBINAR_RESOURCE',
        resourceId: video.id,
        payloadJson: JSON.stringify({
          timeNow: new Date().getTime(),
        }),
      });
    }
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto">
        <Skeleton className="w-[230px] h-[128px] rounded-2xl shrink-0" />
        <Skeleton className="w-[230px] h-[128px] rounded-2xl shrink-0" />
        <Skeleton className="w-[230px] h-[128px] rounded-2xl shrink-0" />
      </div>
    );
  }

  if (!videos || videos.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        <p>{t('adminLive.videos.empty')}</p>
      </div>
    );
  }

  return (
    <div className={`flex gap-4 ${isModeratorLive ? 'flex-row flex-wrap' : 'overflow-x-auto'}`}>
      {videos.map((video) => {
        const isLive = adminResourceStates.some(
          (state) =>
            state.resourceType === 'WEBINAR_RESOURCE' &&
            state.resourceId === video.id &&
            state.live,
        );

        return (
          <div
            key={video.id}
            className="shrink-0 w-[230px] h-[128px] bg-white rounded-2xl flex flex-col items-center justify-center relative overflow-hidden"
          >
            <video
              className="w-full h-full object-cover rounded-2xl"
              src={video.resourceUrl}
              preload="metadata"
              muted
              onLoadedMetadata={(e) => {
                e.currentTarget.pause();
              }}
              onError={() => {
                console.warn('Erreur de chargement de la vidéo:', video.fileName);
              }}
            />

            <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 z-100">
              {isLive ? (
                <Button
                  className="bg-[#FFD753] hover:bg-[#FFD753]/80"
                  onClick={() => handleToggleVideoLive(video)}
                >
                  {t('adminLive.cards.stop')}
                </Button>
              ) : (
                <Button
                  onClick={() => handleToggleVideoLive(video)}
                  style={{
                    backgroundColor:
                      currentWorkspace?.visualIdentity?.secondaryBackgroundColor || undefined,
                    color:
                      currentWorkspace?.visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000',
                  }}
                >
                  {t('adminLive.cards.start')}
                </Button>
              )}
            </div>

            <div className="absolute bottom-0 left-0 py-0.5 px-2 bg-black rounded-bl-2xl rounded-tr-2xl">
              <p className="text-white text-xs">{video.fileName}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
